/**
 * Classifies a thrown value as a DETERMINISTIC database-query failure — one that says the request
 * itself is wrong, so re-issuing it unchanged can only fail the same way.
 *
 * The contrast with `describeTransientNetworkError` is the whole point of this module. A transient
 * failure (offline, canceled, timed out) is fixed by waiting and trying again; a deterministic one
 * is fixed by shipping different code or different data, and every retry in between is pure delay
 * before the user is told anything. On staging this was the difference between a schema drift
 * showing up as an honest "this app is out of date" screen and showing up as a blank tab, a tap
 * that did nothing, and — on the one screen that did surface it — a 15-second spinner followed by
 * raw SQL.
 *
 * Two kinds are distinguished because they mean different things to a user:
 *
 * - `schema-mismatch` — the query names a column or table the database does not have. The data
 *   the app was built against has moved on (or, for a bundled/offline SQLite file, the app has
 *   moved on and the file is stale). The user's remedy is an update; ours is a redeploy.
 * - `invalid-query` — the SQL itself is rejected (too many bound parameters, a syntax error, an
 *   unknown function). That is a bug in how the query was written, not something the user can act
 *   on; it gets the generic failure copy but still skips the retries.
 *
 * Message matching is unavoidable here for the same reason it is in the transient classifier: the
 * text originates in SQLite / Cloudflare D1 and reaches us wrapped in a transport error (`static-db
 * worker query failed (500 …): {"error":"D1_ERROR: no such column: …"}`), so the identity lives
 * only in the message. The tests pin the exact production strings so a wording change fails loudly.
 *
 * Deliberately dependency-free, like the rest of this package (see `is-transient-network-error.ts`).
 */

export type DeterministicQueryErrorKind = "schema-mismatch" | "invalid-query";

/**
 * SQLite's wording for a query that references schema the database lacks. `has no column named`
 * is the INSERT/UPDATE spelling of the same drift; `no such table` covers a whole missing table
 * (a migration the client predates, or a bundled file older than the schema).
 */
const SCHEMA_MISMATCH_PATTERN = /no such (?:column|table|view|index)\b|has no column named/i;

/**
 * SQLite's wording for SQL it refuses to compile or bind. `too many SQL variables` is D1's
 * 100-parameter cap being exceeded by a query the transport's chunker could not split.
 */
const INVALID_QUERY_PATTERN =
  /too many SQL variables|syntax error|no such function|wrong number of arguments|ambiguous column name|no such module/i;

/**
 * How far to walk the `cause` chain. Mirrors the transient classifier: the observed shape is a
 * transport error wrapping the real database error. Bounded, with a visited set, so a cyclic
 * `cause` can never spin.
 */
const MAX_CAUSE_DEPTH = 5;

function classifyText(text: string): DeterministicQueryErrorKind | null {
  if (SCHEMA_MISMATCH_PATTERN.test(text)) {
    return "schema-mismatch";
  }
  if (INVALID_QUERY_PATTERN.test(text)) {
    return "invalid-query";
  }
  return null;
}

/**
 * The deterministic-failure category of `error`, or `null` if it is not a recognisable database
 * query failure.
 *
 * Returns `null` for anything that is not an `Error`, for the same reason the transient classifier
 * does: a non-Error throw is already anomalous, and a bare string should not be able to reclassify
 * itself out of the retry budget.
 */
export function describeDeterministicQueryError(
  error: unknown,
): DeterministicQueryErrorKind | null {
  const visited = new Set<unknown>();
  let current: unknown = error;

  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth += 1) {
    if (!(current instanceof Error) || visited.has(current)) {
      break;
    }
    visited.add(current);

    const kind = classifyText(`${current.name}: ${current.message}`);
    if (kind !== null) {
      return kind;
    }
    current = current.cause;
  }

  return null;
}

/**
 * Whether `error` is a deterministic query failure — one that must NOT be retried, because the
 * request is wrong rather than unlucky.
 *
 * Like `isTransientNetworkError`, this must never be used to skip REPORTING. A deterministic
 * failure is precisely the kind that needs a human to ship a fix; the classification is for
 * choosing the recovery strategy and the user-facing copy, and for tagging the report so it groups
 * apart from network noise.
 */
export function isDeterministicQueryError(error: unknown): boolean {
  return describeDeterministicQueryError(error) !== null;
}
